/**
 * Project Service
 * Handles all API calls related to project management
 */

import { authenticatedFetch } from '../utils/authErrorHandler';

// Get base URL from environment variables
const getBaseUrl = (): string => {
  return import.meta.env.VITE_DEALS_API_BASE_URL || 'https://default-api-url.com';
};

/**
 * Create a new project
 * Note: Username is no longer passed - it's derived from the bearer token by APIM
 */
export const createProject = async (name: string | null = null, conversationMetadata: any = null, type: string | null = null): Promise<any> => {
  const baseUrl = getBaseUrl();

  const response = await authenticatedFetch(`${baseUrl}/api/objects/projects/`, {
    method: 'POST',
    body: JSON.stringify({
      name,
      type,
      conversation: conversationMetadata
    })
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to create project: ${response.status} ${response.statusText} - ${errorText}`);
  }

  const project = await response.json();
  return project;
};

/**
 * Get a project by ID
 */
export const getProject = async (projectId: string): Promise<any> => {
  const baseUrl = getBaseUrl();

  const response = await authenticatedFetch(`${baseUrl}/api/objects/projects/${projectId}`);

  if (!response.ok) {
    if (response.status === 404) {
      throw new Error(`Project ${projectId} not found`);
    }
    const errorText = await response.text();
    throw new Error(`Failed to fetch project: ${response.status} ${response.statusText} - ${errorText}`);
  }

  const project = await response.json();
  return project;
};

/**
 * Get or create a project for the authenticated user
 * Note: Username is no longer passed - it's derived from the bearer token by APIM
 */
export const getOrCreateUserProject = async (conversationData: any = null, type: string | null = null): Promise<any> => {
  // First, try to get existing projects for the authenticated user
  const projects = await getUserProjects(1);

  if (projects && projects.length > 0) {
    return projects[0];
  }

  // If no project exists, create one
  return await createProject(null, conversationData, type);
};

/**
 * Get all projects for the authenticated user
 * Note: Username is no longer passed - it's derived from the bearer token by APIM
 */
export const getUserProjects = async (limit: number = 50): Promise<any[]> => {
  const baseUrl = getBaseUrl();

  const response = await authenticatedFetch(`${baseUrl}/api/objects/projects/?limit=${limit}`);

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to fetch user projects: ${response.status} ${response.statusText} - ${errorText}`);
  }

  const projects = await response.json();
  return projects;
};

/**
 * Update an existing project
 * Note: Username is no longer passed - it's derived from the bearer token by APIM
 */
export const updateProject = async (projectId: string, updates: any): Promise<any> => {
  const baseUrl = getBaseUrl();

  const response = await authenticatedFetch(`${baseUrl}/api/objects/projects/${projectId}`, {
    method: 'PUT',
    body: JSON.stringify(updates)
  });

  if (!response.ok) {
    if (response.status === 404) {
      throw new Error(`Project ${projectId} not found or not authorized`);
    }
    const errorText = await response.text();
    throw new Error(`Failed to update project: ${response.status} ${response.statusText} - ${errorText}`);
  }

  const project = await response.json();
  return project;
};

/**
 * Delete a project
 * Note: Username is no longer passed - it's derived from the bearer token by APIM
 */
export const deleteProject = async (projectId: string): Promise<void> => {
  const baseUrl = getBaseUrl();

  const response = await authenticatedFetch(`${baseUrl}/api/objects/projects/${projectId}`, {
    method: 'DELETE',
  });

  if (!response.ok) {
    if (response.status === 404) {
      throw new Error(`Project ${projectId} not found or not authorized`);
    }
    const errorText = await response.text();
    throw new Error(`Failed to delete project: ${response.status} ${response.statusText} - ${errorText}`);
  }
};

/**
 * Get conversation history for a project
 * Returns the conversation turns, outputs, and workspace state for rehydration
 * @param projectId - The project ID to fetch history for
 * @param signal - Optional AbortSignal for cancellation support
 */
export const getConversationHistory = async (projectId: string, signal?: AbortSignal): Promise<any> => {
  const baseUrl = getBaseUrl();

  try {
    const response = await authenticatedFetch(
      `${baseUrl}/api/objects/projects/${projectId}/conversation-history`,
      { signal }
    );

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error(`Conversation history not found for project ${projectId}`);
      }
      const errorText = await response.text();
      throw new Error(`Failed to fetch conversation history: ${response.status} ${response.statusText} - ${errorText}`);
    }

    const history = await response.json();
    return history;
  } catch (error) {
    // Re-throw abort errors as-is for proper handling upstream
    if (error instanceof Error && error.name === 'AbortError') {
      throw error;
    }
    throw error;
  }
};
